/**
 * Détection d'une nouvelle version d'une app Vite déployée (BO, store-app). Un déploiement remplace
 * `index.html`, servi sans cache, et le nom de son script d'entrée (`/assets/index-<hash>.js`)
 * dépend du contenu du code : s'il diffère de celui de la page chargée, une autre version est en
 * ligne. Un déploiement qui ne touche pas l'app garde le même nom et ne déclenche donc rien.
 */
import { DateTime } from 'luxon'

/** Une vérification toutes les 10 minutes (demande de Baptiste du 16/10). */
export const UPDATE_CHECK_INTERVAL_MS = 10 * 60_000
/** Retour sur l'onglet : on revérifie, mais pas plus d'une fois par minute. */
const VISIBLE_CHECK_MIN_GAP_MS = 60_000
/** Réseau qui ne répond pas : on abandonne la vérification, la suivante réessaiera. */
const FETCH_TIMEOUT_MS = 15_000
const INDEX_URL = '/index.html'
/** Rechargement de nuit : entre 4h et 5h, heure de Paris, les boutiques sont fermées. */
const NIGHTLY_RELOAD_HOUR = 4
/** Version déjà visée par un rechargement de nuit dans cet onglet : on ne recharge pas en boucle. */
const NIGHTLY_RELOAD_KEY = 'gozoki:app-update:nightly-reload'

const SCRIPT_TAG = /<script\b[^>]*>/gi

/** Script d'entrée d'un `index.html` construit par Vite : le premier script module avec `src`. */
export const entryScriptOf = (html: string): string | null => {
  for (const [tag] of html.matchAll(SCRIPT_TAG)) {
    if (!/\btype=["']module["']/i.test(tag)) continue
    const src = /\bsrc=["']([^"']+)["']/i.exec(tag)
    if (src) return src[1]
  }
  return null
}

/** Script d'entrée de la page affichée, lu comme dans `index.html` (attribut brut). */
const loadedEntryScript = (): string | null =>
  document.querySelector('script[type="module"][src]')?.getAttribute('src') ?? null

/** Heure de Paris (0-23) de `date`, quel que soit le fuseau du poste. */
export const parisHour = (date: Date): number =>
  DateTime.fromJSDate(date).setZone('Europe/Paris').hour

/**
 * Surveille les déploiements : `onUpdate` reçoit le nouveau script d'entrée à chaque vérification
 * qui trouve une autre version que celle chargée (donc de nouveau 10 minutes plus tard, tant que la
 * page n'est pas rechargée). Une vérification qui échoue (réseau coupé, réponse en erreur) ne fait
 * rien. Renvoie la fonction d'arrêt.
 */
export const watchAppUpdates = ({
  onUpdate,
}: {
  onUpdate: (latestEntry: string) => void
}): (() => void) => {
  const loaded = loadedEntryScript()
  if (!loaded) return () => {}

  let lastCheck = Number.NEGATIVE_INFINITY
  let inFlight = false
  const check = async () => {
    if (inFlight) return
    inFlight = true
    lastCheck = Date.now()
    try {
      const response = await fetch(INDEX_URL, {
        cache: 'no-store',
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      })
      if (!response.ok) return
      const latest = entryScriptOf(await response.text())
      if (latest && latest !== loaded) onUpdate(latest)
    } catch {
      // Hors ligne ou délai dépassé : la prochaine vérification réessaiera.
    } finally {
      inFlight = false
    }
  }

  const timer = setInterval(check, UPDATE_CHECK_INTERVAL_MS)
  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return
    if (Date.now() - lastCheck >= VISIBLE_CHECK_MIN_GAP_MS) void check()
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  return () => {
    clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}

const readNightlyTarget = (): string | null => {
  try {
    return sessionStorage.getItem(NIGHTLY_RELOAD_KEY)
  } catch {
    return null
  }
}

/**
 * Rechargement de nuit, une seule fois par version : on télécharge d'abord le nouveau script en
 * entier (le cache du navigateur le garde ensuite), pour ne pas laisser une page blanche sur l'écran
 * si le réseau tombe entre-temps. Faux si on n'a pas rechargé.
 */
export const reloadForNightlyUpdate = async (latestEntry: string): Promise<boolean> => {
  if (readNightlyTarget() === latestEntry) return false
  try {
    // Délai borné : un réseau lent ne repousse pas le rechargement après la plage de nuit.
    const response = await fetch(latestEntry, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
    if (!response.ok) return false
    await response.blob()
    sessionStorage.setItem(NIGHTLY_RELOAD_KEY, latestEntry)
  } catch {
    return false
  }
  window.location.reload()
  return true
}

/**
 * Réaction à une nouvelle version : avec `reloadAtNight` (écran de la boutique), entre 4h et 5h
 * heure de Paris, l'app se recharge d'elle-même si `reloadForNightlyUpdate` le permet ; dans tous
 * les autres cas, `notify` prévient l'utilisateur, qui choisit quand recharger.
 */
export const handleNewVersion = async (
  latestEntry: string,
  { reloadAtNight, notify }: { reloadAtNight: boolean; notify: () => void },
): Promise<void> => {
  const nightly = reloadAtNight && parisHour(new Date()) === NIGHTLY_RELOAD_HOUR
  if (nightly && (await reloadForNightlyUpdate(latestEntry))) return
  notify()
}
